import React from "react";

const letterFor = (idx) => String.fromCharCode(65 + idx); // 0->A

/**
 * value format to store:
 * SINGLE: number (1-based index)
 * MULTIPLE: number[] (1-based indices)
 * MAP: { A: 3, B: 1 }
 */

export default function Matching({ mcq, value, setValue, submitted }) {
  const { matchingA = [], matchingB = [], matchingAnswerMode } = mcq;

  const leftLabels = matchingA.map((_, i) => letterFor(i)); // A, B, C...
  const rightIndices = matchingB.map((_, i) => i + 1); // 1, 2, 3...

  // ✅ HANDLERS
  const handleSingleSelect = (idx) => {
    if (submitted) return;
    setValue(idx + 1); // store index (1-based)
  };

  const handleMultipleToggle = (idx) => {
    if (submitted) return;
    let arr = Array.isArray(value) ? [...value] : [];
    const indexVal = idx + 1;
    if (arr.includes(indexVal)) {
      arr = arr.filter((x) => x !== indexVal);
    } else {
      arr.push(indexVal);
    }
    setValue(arr.sort((a, b) => a - b));
  };

  const handleMapSelect = (leftLabel, selectedVal) => {
    if (submitted) return;
    const v = { ...(value || {}) };
    if (!selectedVal) delete v[leftLabel];
    else v[leftLabel] = parseInt(selectedVal, 10);
    setValue(v);
  };

  return (
    <div className="space-y-4">
      {/* Display Pairs */}
      <div className="space-y-1">
        {matchingA.map((aItem, idx) => (
          <p key={idx} className="text-gray-800 dark:text-gray-200">
            <span className="font-bold">{leftLabels[idx]}.</span> {aItem} –{" "}
            {matchingB[idx]}
          </p>
        ))}
      </div>

      {/* Answer Selection */}
      {matchingAnswerMode === "single" && (
        <div className="mt-4 space-y-3">
          {matchingA.map((_, idx) => {
            const indexVal = idx + 1;
            const selected = value === indexVal;
            return (
              <label
                key={idx}
                className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition ${
                  selected
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900 ring-2 ring-blue-400 dark:ring-blue-300"
                    : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                }`}
              >
                <input
                  type="radio"
                  name={`match_single_${mcq._id}`}
                  checked={selected}
                  disabled={submitted}
                  onChange={() => handleSingleSelect(idx)}
                  className="h-4 w-4"
                />
                <span className="text-gray-800 dark:text-gray-200">
                  ({leftLabels[idx].toLowerCase()})
                </span>
              </label>
            );
          })}
        </div>
      )}

      {matchingAnswerMode === "multiple" && (
        <div className="mt-4 space-y-3">
          {matchingA.map((_, idx) => {
            const indexVal = idx + 1;
            const selected = Array.isArray(value) && value.includes(indexVal);
            return (
              <label
                key={idx}
                className={`flex items-center gap-3 p-3 border rounded-md cursor-pointer transition ${
                  selected
                    ? "border-blue-500 bg-blue-50 dark:bg-blue-900 ring-2 ring-blue-400 dark:ring-blue-300"
                    : "border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800"
                }`}
              >
                <input
                  type="checkbox"
                  checked={selected}
                  disabled={submitted}
                  onChange={() => handleMultipleToggle(idx)}
                  className="h-4 w-4"
                />
                <span className="text-gray-800 dark:text-gray-200">
                  ({leftLabels[idx].toLowerCase()})
                </span>
              </label>
            );
          })}
        </div>
      )}

      {matchingAnswerMode === "map" && (
        <div className="mt-4 space-y-4">
          {matchingA.map((_, idx) => {
            const leftLabel = leftLabels[idx];
            const currentSelection = value?.[leftLabel] ?? "";
            return (
              <div key={idx} className="flex items-center gap-3">
                <span className="w-10 font-semibold text-gray-800 dark:text-gray-200">
                  {leftLabel} →
                </span>
                <select
                  className="flex-1 border border-gray-300 dark:border-gray-600 rounded px-3 py-2 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200"
                  disabled={submitted}
                  value={currentSelection}
                  onChange={(e) => handleMapSelect(leftLabel, e.target.value)}
                >
                  <option value="">Select</option>
                  {rightIndices.map((n) => (
                    <option key={n} value={n}>
                      {n} ({matchingB[n - 1]})
                    </option>
                  ))}
                </select>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
